import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

/**
 * Katua upsell carousel (block). Renders the parent section through the Product Recommendations
 * API (Search & Discovery) when it nears the viewport, takes this block's markup from the response
 * by id, falls back to related products when no complementary products are set, and lets
 * shoppers add a size straight to the cart.
 *
 * The cards are swapped in with innerHTML, so handlers query the DOM instead of using refs.
 */
class KatuaUpsell extends Component {
  /** @type {AbortController} */
  #listeners = new AbortController();

  /** @type {AbortController | undefined} */
  #fetchController;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    document.addEventListener('click', this.#handleOutsideClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });

    // Already rendered with products (e.g. the theme editor rendered the recommendations response).
    if (this.dataset.count) {
      this.#afterRender();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners.abort();
    this.#observer?.disconnect();
    this.#fetchController?.abort();
  }

  /**
   * Scrolls the carousel by one card.
   * @param {MouseEvent} event
   */
  scrollTrack(event) {
    const button = event.target;
    const track = this.querySelector('[data-track]');
    const firstItem = track?.firstElementChild;
    if (!(button instanceof HTMLElement) || !(track instanceof HTMLElement) || !(firstItem instanceof HTMLElement)) {
      return;
    }

    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    const isRtl = getComputedStyle(track).direction === 'rtl';
    const direction = Number(button.dataset.direction) || 1;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    track.scrollBy({
      left: (firstItem.offsetWidth + gap) * direction * (isRtl ? -1 : 1),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  /**
   * Opens or closes a card's size panel.
   * @param {MouseEvent} event
   */
  toggleSizes(event) {
    const trigger = event.target;
    if (!(trigger instanceof HTMLButtonElement)) return;

    const panel = this.#panelFor(trigger);
    if (!panel) return;

    const willOpen = trigger.getAttribute('aria-expanded') !== 'true';
    this.#closeAllPanels();
    if (!willOpen) return;

    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');

    const firstSize = panel.querySelector('.katua-upsell__size:not(:disabled)');
    if (firstSize instanceof HTMLElement) firstSize.focus();
  }

  /**
   * Closes the size panel from its own close button and returns focus to the "+" trigger.
   * @param {MouseEvent} event
   */
  closeSizes(event) {
    if (!(event.target instanceof Element)) return;
    const trigger = this.#closeAllPanels();
    trigger?.focus();
  }

  /**
   * Adds one unit of the clicked variant to the cart.
   * @param {MouseEvent} event
   */
  async addVariant(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement) || button.disabled || button.classList.contains('is-loading')) return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    const status = this.querySelector('[data-status]');
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    /** @type {Record<string, unknown>} */
    const body = { items: [{ id: variantId, quantity: 1 }] };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    const cartAddUrl = /** @type {any} */ (window).Theme?.routes?.cart_add_url ?? '/cart/add.js';
    button.classList.add('is-loading');

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message);

      this.dispatchEvent(
        new CartAddEvent({}, String(variantId), {
          source: 'katua-upsell',
          itemCount: 1,
          variantId: String(variantId),
          sections: result.sections,
        })
      );

      button.classList.add('is-added');
      if (status) status.textContent = this.dataset.textAdded ?? '';

      setTimeout(() => {
        button.classList.remove('is-added');
        this.#closeAllPanels();
        if (status) status.textContent = '';
      }, 1200);
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : this.dataset.textError ?? '';
      if (status) status.textContent = message;
    } finally {
      button.classList.remove('is-loading');
    }
  }

  async #load() {
    const { intent = 'complementary', fallback } = this.dataset;

    try {
      let source = await this.#fetchSection(intent);
      if (intent === 'complementary' && fallback === 'true' && this.#countOf(source) === 0) {
        source = await this.#fetchSection('related');
      }

      if (!source || this.#countOf(source) === 0) {
        this.#hideSection();
        return;
      }

      this.innerHTML = source.innerHTML;
      this.dataset.count = String(this.#countOf(source));
      this.#afterRender();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('Katua upsell:', error);
      this.#hideSection();
    }
  }

  /**
   * Renders this section through the recommendations route.
   * @param {string} intent - 'complementary' or 'related'.
   * @returns {Promise<HTMLElement | null>} This section's element from the response.
   */
  async #fetchSection(intent) {
    const { url, productId, sectionId, limit = '8' } = this.dataset;
    if (!url || !productId || !sectionId) return null;

    const params = new URLSearchParams({ product_id: productId, section_id: sectionId, limit, intent });

    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    const response = await fetch(`${url}?${params}`, { signal: this.#fetchController.signal });
    if (!response.ok) return null;

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const element = html.getElementById(this.id);
    return element instanceof HTMLElement ? element : null;
  }

  /**
   * @param {HTMLElement | null} element
   * @returns {number}
   */
  #countOf(element) {
    return Number(element?.dataset.count || 0);
  }

  #hideSection() {
    if (/** @type {any} */ (window).Shopify?.designMode) return;
    this.hidden = true;
  }

  #afterRender() {
    const track = this.querySelector('[data-track]');
    if (!(track instanceof HTMLElement)) return;

    const { signal } = this.#listeners;
    const update = () => this.#updateArrows(track);
    track.addEventListener('scroll', update, { passive: true, signal });
    window.addEventListener('resize', update, { passive: true, signal });
    update();
  }

  /**
   * @param {HTMLElement} track
   */
  #updateArrows(track) {
    const maxScroll = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);

    for (const arrow of this.querySelectorAll('.katua-upsell__arrow')) {
      if (!(arrow instanceof HTMLButtonElement)) continue;
      const isPrevious = arrow.dataset.direction === '-1';
      arrow.disabled = isPrevious ? position <= 2 : position >= maxScroll - 2;
    }
  }

  /**
   * @param {HTMLElement} trigger
   * @returns {HTMLElement | null}
   */
  #panelFor(trigger) {
    const id = trigger.getAttribute('aria-controls');
    const panel = id ? this.querySelector(`[id="${id}"]`) : null;
    return panel instanceof HTMLElement ? panel : null;
  }

  /**
   * @returns {HTMLButtonElement | undefined} The trigger of the open size panel, if any.
   */
  #closeAllPanels() {
    /** @type {HTMLButtonElement | undefined} */
    let openTrigger;

    for (const trigger of this.querySelectorAll('.katua-upsell__quick[aria-expanded="true"]')) {
      if (!(trigger instanceof HTMLButtonElement)) continue;
      openTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'false');
      const panel = this.#panelFor(trigger);
      if (panel) panel.hidden = true;
    }

    return openTrigger;
  }

  /**
   * @param {MouseEvent} event
   */
  #handleOutsideClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.katua-upsell__sizes, .katua-upsell__quick')) return;
    this.#closeAllPanels();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const trigger = this.#closeAllPanels();
    trigger?.focus();
  };
}

if (!customElements.get('katua-upsell')) {
  customElements.define('katua-upsell', KatuaUpsell);
}
